/**
 * Timeline layout maths.
 *
 * Kept out of the component so the positioning can be tested directly — a
 * timeline that places events wrongly still renders happily, and the error is
 * only visible if you know the dates by heart.
 */

const DAY = 86400000

/** Fraction of the axis two events must be apart to share a lane. */
const LANE_GAP_RATIO = 0.07

/** Minimum padding at each end so the first/last event is not flush to the edge. */
const MIN_PADDING = 30 * DAY

const timeOf = (value) => {
  const date = new Date(value)
  return isNaN(date.getTime()) ? null : date.getTime()
}

/**
 * Builds everything the timeline needs to draw itself.
 *
 * Positions are on a real time axis, not by index, so a five-year gap looks
 * like a five-year gap. Returns null when there is nothing datable to show.
 *
 * @param {Array} events  passport events
 * @param {number} now    injected so tests are not time-dependent
 */
export const buildTimelineModel = (events = [], now = Date.now()) => {
  const dated = (Array.isArray(events) ? events : [])
    .map(event => {
      const start = timeOf(event.startDate)
      if (start === null) return null
      return {
        id: event._id,
        name: event.name,
        venueName: event.venueName || '',
        start,
        end: timeOf(event.endDate) ?? start
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start)

  if (dated.length === 0) return null

  // `now` is folded into the range so the marker is always on the axis, even
  // when every event is in the past or all of them are still to come.
  const earliest = Math.min(dated[0].start, now)
  const latest = Math.max(...dated.map(e => e.end), now)
  const padding = Math.max((latest - earliest) * 0.06, MIN_PADDING)

  const min = earliest - padding
  const max = latest + padding
  const span = max - min || 1

  const positionOf = (time) => ((time - min) / span) * 100

  // Greedy lane packing: take the first lane whose previous occupant ended far
  // enough back that the two cards will not overlap.
  const laneEnds = []
  const minGap = span * LANE_GAP_RATIO

  const placed = dated.map(event => {
    let lane = laneEnds.findIndex(end => event.start - end > minGap)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(event.end)
    } else {
      laneEnds[lane] = event.end
    }

    return {
      ...event,
      lane,
      left: positionOf(event.start),
      isPast: event.end <= now
    }
  })

  const startYear = new Date(min).getFullYear()
  const endYear = new Date(max).getFullYear()
  const years = []
  for (let year = startYear; year <= endYear; year += 1) {
    const time = Date.UTC(year, 0, 1)
    if (time >= min && time <= max) years.push({ year, left: positionOf(time) })
  }

  return {
    placed,
    years,
    nowLeft: positionOf(now),
    laneCount: Math.max(1, laneEnds.length)
  }
}

export default buildTimelineModel
